'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { FlashS } from '@lichtblick/photon/icons/energy/flash-s';
import { Checkbox } from '@lichtblick/photon/molecules/checkbox';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { useTracker } from '@lichtblick/tracker';
import { useSearchParams } from 'next/navigation';
import { type ChangeEventHandler, type FC, Suspense, useCallback, useEffect, useState } from 'react';
import type { CalculatorElectricityType } from '../../types/storyblok';
import { CityInput } from '../OAllInOneCalculator/CityInput';
import {
  ELECTRICITY_USAGE,
  gasUsagePerSquareMeter,
  squareMeterLimit,
  usageLimitElectricity,
  usageLimitGas,
} from '../OAllInOneCalculator/constants';
import { UsageModal } from '../OAllInOneCalculator/UsageModal';
import { useVpCodes } from '../OAllInOneCalculator/useVpCodes';
import styles from './calculator.module.scss';

const CalculatorElectricityPlaceholder: FC = () => (
  <div>
    <div aria-hidden="true" className={styles.form}>
      <Headline className={styles.headline} renderAs="h2" size="s">
        ÖkoStrom*
        <FlashS />
      </Headline>

      <Textfield disabled label="Ort" readOnly value="" hideLabelSuffix />

      <Stepper disabled label="Personenanzahl" max={10} min={1} readOnly value={1} hideLabelSuffix />

      <Textfield
        disabled
        label="Stromverbrauch"
        readOnly
        rightSlot={<Text size="s">kWh/Jahr</Text>}
        value={String(ELECTRICITY_USAGE[0])}
        hideLabelSuffix
      />

      <Checkbox label={<Checkbox.Label>LichtBlick Gas hinzufügen</Checkbox.Label>}>
        <Checkbox.Checkbox disabled />
      </Checkbox>

      <BoxCTA as="button" disabled type="button">
        Tarife berechnen
      </BoxCTA>
    </div>
  </div>
);

const InnerCalculatorElectricity: FC = () => {
  const params = useSearchParams();
  const [zip, setZip] = useState('');
  const [city, setCity] = useState('');
  const initialPeople = Number.parseInt(params.get('household') || '1', 10);

  const [people, setPeople] = useState(initialPeople);
  const [electricityUsage, setElectricityUsage] = useState(params.get('consumption') || String(ELECTRICITY_USAGE[0]));

  const [addGas, setAddGas] = useState(false);
  const [space, setSpace] = useState(squareMeterLimit.default);
  const [gasUsage, setGasUsage] = useState(String(space * gasUsagePerSquareMeter));

  const [electricityUsageModalOpen, setElectricityUsageModalOpen] = useState(false);
  const [gasUsageModalOpen, setGasUsageModalOpen] = useState(false);

  const { computeVpCodes } = useVpCodes();
  const { trackCustomEvent } = useTracker();

  const cityChangeHandler = useCallback((city: string, zip: string) => {
    setCity(city);
    setZip(zip);
  }, []);

  const electricityChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setElectricityUsage(e.currentTarget.value);
  }, []);

  const addGasChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setAddGas(e.currentTarget.checked);
  }, []);

  const gasChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setGasUsage(e.currentTarget.value);
  }, []);

  const [vpCodes, setVpCodes] = useState<{
    uvp?: string;
    vp?: string;
  }>({});

  useEffect(() => {
    setVpCodes(computeVpCodes());
  }, [computeVpCodes]);

  return (
    <>
      <form
        action="/checkout/"
        className={styles.form}
        id="oekostrom-calculator"
        method="GET"
        onSubmit={(e) => {
          if (Number(electricityUsage) > usageLimitElectricity.max) {
            e.preventDefault();
            setElectricityUsageModalOpen(true);

            return;
          }

          if (addGas && Number(gasUsage) > usageLimitGas.max) {
            e.preventDefault();
            setGasUsageModalOpen(true);

            return;
          }

          if (e.currentTarget.checkValidity()) {
            trackCustomEvent({
              eventName: 'tariff_calculator_show_tariffs',
              customProps: {
                product: 'ÖkoStrom',
                zip_code: zip,
                people_counter: people,
                consumption_calculator: `${electricityUsage} kWh/Jahr`,
                bundle_product: addGas,
              },
            });
          }
        }}
      >
        <Headline className={styles.headline} renderAs="h2" size="s">
          ÖkoStrom*
          <FlashS />
        </Headline>
        <CityInput onChange={cityChangeHandler} defaultZip={params.get('plz') || undefined} />
        <Stepper
          defaultValue={people}
          label="Personenanzahl"
          max={10}
          min={1}
          onChange={(value) => {
            setPeople(value);
            setElectricityUsage(String(ELECTRICITY_USAGE[Math.min(Math.max(value - 1, 0), 9)]));
          }}
          hideLabelSuffix
        />
        <Textfield
          inputMode="numeric"
          label="Stromverbrauch"
          min={usageLimitElectricity.min}
          name="strom"
          onChange={electricityChangeHandler}
          rightSlot={
            <Text className={styles.unit} size="s">
              kWh/Jahr
            </Text>
          }
          type="number"
          value={electricityUsage}
          required
          hideLabelSuffix
        />
        <Checkbox label={<Checkbox.Label>LichtBlick Gas hinzufügen</Checkbox.Label>}>
          <Checkbox.Checkbox onChange={addGasChangeHandler} />
        </Checkbox>
        {addGas && (
          <>
            <Stepper
              defaultValue={space}
              label="Wohnfläche in m²"
              max={squareMeterLimit.max}
              min={squareMeterLimit.min}
              onChange={(value) => {
                setSpace(value);
                setGasUsage(String(value * gasUsagePerSquareMeter));
              }}
              step={squareMeterLimit.step}
              hideLabelSuffix
            />
            <Textfield
              label="Gasverbrauch"
              min={usageLimitGas.min}
              name="gas"
              onChange={gasChangeHandler}
              rightSlot={<Text size="s">kWh/Jahr</Text>}
              type="number"
              value={gasUsage}
              hideLabelSuffix
            />
          </>
        )}

        <input name="ort" type="hidden" value={city} />
        <input name="plz" type="hidden" value={zip} />
        {Boolean(vpCodes.vp) && <input name="vp" type="hidden" value={vpCodes.vp} />}
        {Boolean(vpCodes.uvp) && <input name="uvp" type="hidden" value={vpCodes.uvp} />}

        <BoxCTA as="button" type="submit">
          Tarife berechnen
        </BoxCTA>
      </form>
      <UsageModal isOpen={electricityUsageModalOpen} route="/gewerbestrom" setIsOpen={setElectricityUsageModalOpen} />
      <UsageModal isOpen={gasUsageModalOpen} route="/gewerbegas" setIsOpen={setGasUsageModalOpen} />
    </>
  );
};

// fixes https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
export const CalculatorElectricity: FC<CalculatorElectricityType> = () => (
  <Suspense fallback={<CalculatorElectricityPlaceholder />}>
    <InnerCalculatorElectricity />
  </Suspense>
);
